import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { browserStorage, forgetEverything, isKeepOn, keepMessage, loadKept, saveKept, setKeepOn, wipeKept, type KeepFailure } from "@/lib/deviceKeep";

export interface DeviceKeep {
  on: boolean;
  /** Last successful save on this device, ISO time, or null. */
  savedAt: string | null;
  /** Plain-words problem to show next to the toggle, or null. */
  problem: string | null;
  setOn(next: boolean): void;
  /** Delete the saved copy and switch keeping off. The data on screen is untouched. */
  forget(): void;
}

/**
 * React wiring for lib/deviceKeep. `snapshot` is what to keep; `apply` receives the saved copy once on mount
 * (only when keeping was left on). Autosave starts after that, so the saved copy is never overwritten by an
 * empty first render. `isEmpty` snapshots remove the saved copy instead of saving nothing.
 */
export function useDeviceKeep<T>(toolId: string, snapshot: T, opts: { apply: (saved: T) => void; validate?: (raw: unknown) => T | null; isEmpty?: (s: T) => boolean }): DeviceKeep {
  const storage = useMemo(browserStorage, []);
  const [on, setOnState] = useState(() => isKeepOn(storage, toolId));
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [failure, setFailure] = useState<KeepFailure | null>(null);
  const [ready, setReady] = useState(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (isKeepOn(storage, toolId)) {
      const r = loadKept<T>(storage, toolId, optsRef.current.validate);
      if (r.status === "ok") { optsRef.current.apply(r.data); setSavedAt(r.savedAt); }
      else if (r.status === "error") setFailure(r.error);
    }
    setReady(true);
  }, [storage, toolId]);

  const text = useMemo(() => JSON.stringify(snapshot), [snapshot]);
  useEffect(() => {
    if (!ready || !on) return;
    if (optsRef.current.isEmpty?.(snapshot)) { wipeKept(storage, toolId); setSavedAt(null); return; }
    const r = saveKept(storage, toolId, snapshot);
    if (r.ok) { setSavedAt(r.savedAt); setFailure(null); } else setFailure(r.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, on, text]);

  const setOn = useCallback((next: boolean) => {
    const r = setKeepOn(storage, toolId, next);
    if (!r.ok) { setFailure(r.error); setOnState(false); return; }
    setOnState(next); setFailure(null);
    if (!next) setSavedAt(null);
  }, [storage, toolId]);
  const forget = useCallback(() => { forgetEverything(storage, toolId); setOnState(false); setSavedAt(null); setFailure(null); }, [storage, toolId]);

  return { on, savedAt, problem: failure ? keepMessage[failure] : null, setOn, forget };
}
